import { useState } from 'react';
import { Avatar } from '../components/parts';
import { colorForId } from '../game/cosmetics';
import type { GameSession } from '../game/useGameSession';
import { useT } from '../i18n';

const MIN_PLAYERS = 4;
const TURN_LIMITS = [30, 60, 120];
const DIFFICULTIES = ['easy', 'normal', 'hard'] as const;

export function Lobby({ session }: { session: GameSession }) {
  const t = useT();
  const { room, players, isHost, myUid, busy } = session;
  const [copied, setCopied] = useState(false);

  const canStart = players.length >= MIN_PLAYERS;
  const turnLimit = room?.turnLimit ?? 60;
  const botDifficulty = room?.botDifficulty ?? 'normal';
  const isPublic = room?.isPublic ?? false;
  const timerLabel = (s: number) => (s < 60 ? `${s}${t.home.sec}` : `${s / 60}${t.home.min}`);

  function copyCode() {
    if (!room) return;
    navigator.clipboard?.writeText(room.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  // Share a join link — opening it drops a signed-in friend straight into the room.
  function invite() {
    if (!room) return;
    const url = `${location.origin}/?code=${room.code}`;
    if (navigator.share) {
      navigator.share({ title: 'NEONFALL', url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  }

  return (
    <div className="screen lobby-screen">
      <div className="row between lobby-head">
        <h2 className="h2">{t.lobby.title}</h2>
        <button className="link" onClick={session.leaveToHome}>{t.lobby.leave}</button>
      </div>

      <div className="panel col center code-panel" style={{ gap: 6 }}>
        <div className="eyebrow">{t.lobby.accessCode}</div>
        <div className="pill-code">{room?.code ?? '------'}</div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn small cyan" onClick={copyCode}>
            {copied ? t.lobby.copied : t.lobby.copy}
          </button>
          <button className="btn small magenta" onClick={invite}>
            {t.lobby.invite}
          </button>
        </div>
        <div className="mono-dim" style={{ fontSize: 11 }}>{t.lobby.share}</div>
      </div>

      {/* Match setup — host tweaks non-default options; everyone else sees them. */}
      <div className="panel col setup-panel">
        <div className="eyebrow">{isHost ? t.lobby.setup : t.lobby.setupLocked}</div>
        <div className="setup-row">
          <span className="field-label">{t.home.turnTimer}</span>
          <div className={'segmented' + (isHost ? '' : ' readonly')}>
            {TURN_LIMITS.map((s) => (
              <button
                key={s}
                className={'segmented-btn' + (turnLimit === s ? ' active' : '')}
                onClick={() => session.updateRoomSettings({ turnLimit: s })}
              >
                {timerLabel(s)}
              </button>
            ))}
          </div>
        </div>
        <div className="setup-row">
          <span className="field-label">{t.home.botDiff}</span>
          <div className={'segmented' + (isHost ? '' : ' readonly')}>
            {DIFFICULTIES.map((d) => (
              <button
                key={d}
                className={'segmented-btn' + (botDifficulty === d ? ' active' : '')}
                onClick={() => session.updateRoomSettings({ botDifficulty: d })}
              >
                {t.home.diff[d]}
              </button>
            ))}
          </div>
        </div>
        <div className="setup-row">
          <span className="field-label">{t.lobby.visibility}</span>
          <div className={'segmented' + (isHost ? '' : ' readonly')}>
            <button
              className={'segmented-btn' + (!isPublic ? ' active' : '')}
              onClick={() => session.updateRoomSettings({ isPublic: false })}
            >
              {t.lobby.private}
            </button>
            <button
              className={'segmented-btn' + (isPublic ? ' active' : '')}
              onClick={() => session.updateRoomSettings({ isPublic: true })}
            >
              {t.lobby.public}
            </button>
          </div>
        </div>
        {isPublic && (
          <div className="mono-dim setup-hint">{t.lobby.publicHint}</div>
        )}
      </div>

      <div className="col lobby-runners" style={{ gap: 8 }}>
        <div className="eyebrow">{t.lobby.runners} · {players.length}/8</div>
        <div className="seat-list">
        {players.map((p) => (
          <div key={p.uid} className={'seat' + (p.uid === myUid ? ' me' : '')}>
            <Avatar player={p} />
            <div className="col" style={{ gap: 2, flex: 1 }}>
              <span style={{ fontWeight: 700, color: colorForId(p.nameColor) }}>{p.displayName}</span>
            </div>
            {p.bot && <span className="tag" style={{ color: 'var(--violet)' }}>{t.lobby.bot}</span>}
            {room?.hostUid === p.uid && <span className="tag" style={{ color: 'var(--amber)' }}>{t.lobby.host}</span>}
            {p.uid === myUid && <span className="tag" style={{ color: 'var(--cyan)' }}>{t.lobby.you}</span>}
            {p.bot && isHost && (
              <button
                className="seat-remove"
                aria-label="remove bot"
                onClick={() => session.removeBot(p.uid)}
              >
                ✕
              </button>
            )}
          </div>
        ))}
        </div>
        {players.length < MIN_PLAYERS && (
          <div className="mono-dim" style={{ fontSize: 12 }}>{t.lobby.scanning}</div>
        )}
      </div>

      <div className="spacer" />

      <div className="lobby-actions">
      {isHost ? (
        <div className="col" style={{ gap: 8 }}>
          {players.length < 8 && (
            <button className="btn block ghost" disabled={busy} onClick={session.addBot}>
              + {t.lobby.addBot}
            </button>
          )}
          {!canStart && (
            <div className="mono-dim" style={{ textAlign: 'center', fontSize: 12 }}>
              {t.lobby.needMore}
            </div>
          )}
          <button className="btn block magenta" disabled={!canStart || busy} onClick={session.startGame}>
            {t.lobby.start}
          </button>
        </div>
      ) : (
        <div className="mono-dim" style={{ textAlign: 'center' }}>{t.lobby.waitHost}</div>
      )}
      </div>
    </div>
  );
}
